import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { userNormalLogout, supportLogout } from "../actions";

export interface ReportsState {
  isFetchingReportsData: boolean;
  isExportReport: boolean;
  allReportsList: any[];
  allReportsByGroup: Record<string, any>;
  logEntity: Record<string, any>;
  inventoryIntegrationLogs: any[];
  isFetchingInventoryLogs: boolean;
  inventoryLogsError: string | null;
  inventoryLogsLastKey: any;
  inventoryLogsHasMore: boolean;
  supplierAggregator: string | null;
}

const initialState: ReportsState = {
  isFetchingReportsData: false,
  isExportReport: false,
  allReportsList: [],
  allReportsByGroup: {},
  logEntity: {},
  inventoryIntegrationLogs: [],
  isFetchingInventoryLogs: false,
  inventoryLogsError: null,
  inventoryLogsLastKey: null,
  inventoryLogsHasMore: false,
  supplierAggregator: null,
};

function handleSaveInventoryLogs(
  state: ReportsState,
  payload: any[] | { logs: any[]; lastKey?: any; append?: boolean }
) {
  if (Array.isArray(payload)) {
    state.inventoryIntegrationLogs = payload;
    state.inventoryLogsLastKey = null;
    state.inventoryLogsHasMore = false;
  } else {
    const { logs = [], lastKey = null, append = false } = payload || {};
    if (append) {
      state.inventoryIntegrationLogs = [...state.inventoryIntegrationLogs, ...logs];
    } else {
      state.inventoryIntegrationLogs = logs;
    }
    state.inventoryLogsLastKey = lastKey;
    state.inventoryLogsHasMore = !!lastKey;
  }
}

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    getAllReportsData: (state) => {
      state.isFetchingReportsData = true;
    },
    saveAllReportsData: (state, action: PayloadAction<any>) => {
      state.allReportsList = action.payload?.reportList ?? [];
      state.isFetchingReportsData = false;
    },
    saveReportDataByGroup: (state, action: PayloadAction<any>) => {
      state.allReportsByGroup = action.payload ?? {};
    },
    exportReportData: (state, _action: PayloadAction<any>) => {
      state.isExportReport = true;
    },

    getLogEntity: (state) => {},
    saveLogEntity: (state, action: PayloadAction<any>) => {
      state.logEntity = action.payload ?? {};
    },

    getAuditLogs: (state, action) => {},
    aggregateSyncHealth: (state, action) => {},
    syncHealth: (state, action) => {},
    syncHealthSession: (state, action) => {},
    syncHealthManualSync: (state, action) => {},

    getInventoryIntegrationLogs: (state, _action: PayloadAction<any>) => {
      state.isFetchingInventoryLogs = true;
      state.inventoryLogsError = null;
    },

    saveInventoryIntegrationLogs: (
      state,
      action: PayloadAction<any[] | { logs: any[]; lastKey?: any; append?: boolean }>
    ) => {
      state.isFetchingInventoryLogs = false;
      handleSaveInventoryLogs(state, action.payload);
    },

    failInventoryIntegrationLogs: (state, action: PayloadAction<string | null>) => {
      state.isFetchingInventoryLogs = false;
      state.inventoryLogsError = action.payload ?? "Failed to fetch inventory logs";
    },

    clearInventoryIntegrationLogs: (state) => {
      state.inventoryIntegrationLogs = [];
      state.inventoryLogsLastKey = null;
      state.inventoryLogsHasMore = false;
    },
    setSupplierAggregator: (state, action: PayloadAction<string | null>) => {
      state.supplierAggregator = action.payload;
    },
    openReportsModule: (state) => {
      console.log("Reports module opened");
    },
    sendUploadTargetTemplateFile: (state, action) => {},
    bulkUploadTargetSalesData: (state, action) => {}
  },
  extraReducers: (builder) => {
    builder
      .addCase(userNormalLogout, () => initialState)
      .addCase(supportLogout, () => initialState);
  },
});

export const {
  getAllReportsData,
  saveAllReportsData,
  saveReportDataByGroup,
  exportReportData,
  getLogEntity,
  saveLogEntity,
  getAuditLogs,
  aggregateSyncHealth,
  syncHealth,
  syncHealthSession,
  syncHealthManualSync,
  getInventoryIntegrationLogs,
  saveInventoryIntegrationLogs,
  failInventoryIntegrationLogs,
  clearInventoryIntegrationLogs,
  setSupplierAggregator,
  openReportsModule,
  sendUploadTargetTemplateFile,
  bulkUploadTargetSalesData
} = reportsSlice.actions;

export default reportsSlice.reducer;
